import React from 'react'
import clsx from 'clsx'
import { ArrowDownRight, ArrowUpRight, Minus, type LucideIcon } from 'lucide-react'
import { computeDelta } from '@/lib/dashboardFormat'

export type MetricAccent = 'blue' | 'green' | 'red' | 'purple' | 'orange' | 'slate'

const accentStyles: Record<MetricAccent, { chip: string; icon: string; value: string }> = {
  blue: {
    chip: 'bg-blue-50 dark:bg-blue-500/15',
    icon: 'text-[#2563EB]',
    value: 'text-foreground',
  },
  green: {
    chip: 'bg-green-50 dark:bg-green-500/15',
    icon: 'text-[#16A34A]',
    value: 'text-foreground',
  },
  red: {
    chip: 'bg-red-50 dark:bg-red-500/15',
    icon: 'text-[#DC2626]',
    value: 'text-foreground',
  },
  purple: {
    chip: 'bg-purple-50 dark:bg-purple-500/15',
    icon: 'text-[#9333EA]',
    value: 'text-foreground',
  },
  orange: {
    chip: 'bg-orange-50 dark:bg-orange-500/15',
    icon: 'text-[#EA580C]',
    value: 'text-foreground',
  },
  slate: {
    chip: 'bg-card-muted',
    icon: 'text-muted-foreground',
    value: 'text-foreground',
  },
}

const toneStyles = {
  positive: 'text-[#16A34A] bg-green-50 dark:bg-green-500/15',
  negative: 'text-[#DC2626] bg-red-50 dark:bg-red-500/15',
  neutral: 'text-muted-foreground bg-card-muted',
} as const

interface MetricCardProps {
  label: string
  value: string
  icon: LucideIcon
  accent?: MetricAccent
  /** Current and previous raw numbers drive the variation badge */
  current?: number
  previous?: number
  comparisonLabel?: string
  /** For metrics where going down is good, such as losses */
  lowerIsBetter?: boolean
  helper?: string
  loading?: boolean
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  icon: Icon,
  accent = 'blue',
  current,
  previous,
  comparisonLabel,
  lowerIsBetter = false,
  helper,
  loading = false,
}) => {
  const style = accentStyles[accent]
  const hasComparison = current !== undefined && previous !== undefined
  const delta = hasComparison ? computeDelta(current, previous, lowerIsBetter) : null

  if (loading) {
    return (
      <div className="min-h-[152px] rounded-[20px] border border-border bg-card px-5 py-5 shadow-sm">
        <div className="animate-pulse">
          <div className="mb-4 h-10 w-10 rounded-full bg-card-muted" />
          <div className="mb-2 h-8 w-2/3 rounded bg-card-muted" />
          <div className="mb-3 h-4 w-1/2 rounded bg-card-muted" />
          <div className="h-4 w-3/4 rounded bg-card-muted" />
        </div>
      </div>
    )
  }

  const ToneIcon =
    delta?.tone === 'positive' ? ArrowUpRight : delta?.tone === 'negative' ? ArrowDownRight : Minus

  return (
    <div
      className={clsx(
        'flex min-h-[152px] flex-col justify-between rounded-[20px] border border-border bg-card px-5 py-5 shadow-sm',
        'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md',
      )}
    >
      <div className="flex flex-col gap-3">
        <div className={clsx('flex h-10 w-10 items-center justify-center rounded-full', style.chip)}>
          <Icon className={clsx('h-5 w-5', style.icon)} strokeWidth={2} />
        </div>
        <div className="flex flex-col gap-1">
          <p
            className={clsx(
              'text-[28px] font-bold leading-none tracking-tight',
              style.value,
            )}
          >
            {value}
          </p>
          <p className="text-[13px] font-medium text-muted-foreground">{label}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-1">
        {delta && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className={clsx(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-semibold',
                toneStyles[delta.tone],
              )}
            >
              <ToneIcon className="h-3 w-3" strokeWidth={2.5} />
              {delta.label}
            </span>
            {comparisonLabel && (
              <span className="text-[11px] text-muted-foreground">{comparisonLabel}</span>
            )}
          </div>
        )}
        {helper && <p className="text-[11px] leading-snug text-muted-foreground">{helper}</p>}
      </div>
    </div>
  )
}

export default MetricCard
